import React from 'react';
import { Button } from '@/components/ui/button';
import DateInputDMY from '../DateInputDMY';

/**
 * Comprehensive filter bar for the Sales Invoice list. Exposes one filter
 * per visible table column so every header is searchable:
 *   Invoice # / Date / Due Date / Customer / Sales Rep / Total / Status /
 *   Approval / Overdue.
 *
 * Layout: 3 collapsible-feeling rows in a 1/2/3/4-col responsive grid.
 *   Row 1 — Free-text search (col-span 2) | Status | Approval
 *   Row 2 — Customer | Sales Rep | Overdue | Outstanding-only toggle
 *   Row 3 — Invoice date range | Due date range | Total range | Clear button
 */
const InvoiceFilters = ({
  searchTerm,
  setSearchTerm,
  filterStatus,
  setFilterStatus,
  filterApproval,
  setFilterApproval,
  filterCustomerId,
  setFilterCustomerId,
  customers = [],
  filterSalesRepId,
  setFilterSalesRepId,
  salesReps = [],
  filterOverdue,
  setFilterOverdue,
  outstandingOnly,
  setOutstandingOnly,
  dateFilter,
  setDateFilter,
  dueDateFilter,
  setDueDateFilter,
  totalRange,
  setTotalRange,
  onClearFilters,
}) => {
  const inputCls = 'w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1';

  const anyActive =
    searchTerm ||
    filterStatus !== 'all' ||
    filterApproval !== 'all' ||
    filterCustomerId !== 'all' ||
    filterSalesRepId !== 'all' ||
    filterOverdue !== 'all' ||
    outstandingOnly ||
    dateFilter.start || dateFilter.end ||
    dueDateFilter.start || dueDateFilter.end ||
    totalRange.min !== '' || totalRange.max !== '';

  return (
    <div className="mb-6 space-y-3">
      {/* Row 1: Free-text + Status + Approval */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className={labelCls}>🔍 Search Invoice # / Customer / Sales Rep</label>
          <input
            type="text"
            placeholder="Type any part of an invoice number, customer or sales-rep name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={inputCls}
            data-testid="invoice-search-input"
          />
        </div>
        <div>
          <label className={labelCls}>Payment Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={inputCls}
            data-testid="invoice-status-filter"
          >
            <option value="all">All</option>
            <option value="Pending">Pending (incl. Partially Paid)</option>
            <option value="Paid">Paid</option>
            <option value="Partially Paid">Partially Paid only</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Approval</label>
          <select
            value={filterApproval}
            onChange={(e) => setFilterApproval(e.target.value)}
            className={inputCls}
            data-testid="invoice-approval-filter"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="pending_any">Pending (any level)</option>
            <option value="pending_l1">Pending L1</option>
            <option value="pending_l2">Pending L2</option>
            <option value="approved">Approved</option>
          </select>
        </div>
      </div>

      {/* Row 2: Customer + Sales Rep + Overdue + Outstanding-only */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className={labelCls}>Customer</label>
          <select
            value={filterCustomerId}
            onChange={(e) => setFilterCustomerId(e.target.value)}
            className={inputCls}
            data-testid="invoice-filter-customer"
          >
            <option value="all">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Sales Rep</label>
          <select
            value={filterSalesRepId}
            onChange={(e) => setFilterSalesRepId(e.target.value)}
            className={inputCls}
            data-testid="invoice-filter-sales-rep"
          >
            <option value="all">All sales reps</option>
            {salesReps.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Overdue</label>
          <select
            value={filterOverdue}
            onChange={(e) => setFilterOverdue(e.target.value)}
            className={inputCls}
            data-testid="invoice-overdue-filter"
          >
            <option value="all">All</option>
            <option value="overdue">Overdue only</option>
            <option value="not_overdue">Not overdue</option>
          </select>
        </div>
        <div className="flex items-end">
          <label
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded bg-white text-sm cursor-pointer hover:bg-gray-50 select-none w-full"
            data-testid="invoice-outstanding-only-toggle"
          >
            <input
              type="checkbox"
              checked={!!outstandingOnly}
              onChange={(e) => setOutstandingOnly(e.target.checked)}
              className="rounded"
            />
            Outstanding only (hide Paid)
          </label>
        </div>
      </div>

      {/* Row 3: Invoice date range + Due date range + Total range + Clear */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <label className={labelCls}>Invoice Date — From / To</label>
          <div className="grid grid-cols-2 gap-2">
            <DateInputDMY
              value={dateFilter.start}
              onChange={(e) => setDateFilter((p) => ({ ...p, start: e.target.value }))}
              className={inputCls}
            />
            <DateInputDMY
              value={dateFilter.end}
              onChange={(e) => setDateFilter((p) => ({ ...p, end: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Due Date — From / To</label>
          <div className="grid grid-cols-2 gap-2">
            <DateInputDMY
              value={dueDateFilter.start}
              onChange={(e) => setDueDateFilter((p) => ({ ...p, start: e.target.value }))}
              className={inputCls}
            />
            <DateInputDMY
              value={dueDateFilter.end}
              onChange={(e) => setDueDateFilter((p) => ({ ...p, end: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Total Min</label>
          <input
            type="number"
            placeholder="Min"
            value={totalRange.min}
            onChange={(e) => setTotalRange((p) => ({ ...p, min: e.target.value }))}
            className={inputCls}
            data-testid="invoice-total-min"
          />
        </div>
        <div>
          <label className={labelCls}>Total Max</label>
          <div className="flex gap-1">
            <input
              type="number"
              placeholder="Max"
              value={totalRange.max}
              onChange={(e) => setTotalRange((p) => ({ ...p, max: e.target.value }))}
              className={inputCls}
              data-testid="invoice-total-max"
            />
            <Button
              onClick={onClearFilters}
              variant="outline"
              className="shrink-0"
              data-testid="clear-filters-btn"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {anyActive && (
        <div className="flex items-center flex-wrap gap-2 text-xs text-gray-600 bg-blue-50 border border-blue-100 px-2 py-1.5 rounded">
          <span className="font-medium">Active:</span>
          {searchTerm && <Chip>"{searchTerm}"</Chip>}
          {filterStatus !== 'all' && <Chip>Status: {filterStatus}</Chip>}
          {filterApproval !== 'all' && (
            <Chip>
              Approval: {
                {
                  draft: 'Draft',
                  pending_any: 'Pending (any)',
                  pending_l1: 'Pending L1',
                  pending_l2: 'Pending L2',
                  approved: 'Approved',
                }[filterApproval] || filterApproval
              }
            </Chip>
          )}
          {filterCustomerId !== 'all' && (
            <Chip>Customer: {(customers.find((c) => c.id === filterCustomerId) || {}).name || filterCustomerId}</Chip>
          )}
          {filterSalesRepId !== 'all' && (
            <Chip>Sales Rep: {(salesReps.find((r) => r.id === filterSalesRepId) || {}).name || filterSalesRepId}</Chip>
          )}
          {filterOverdue !== 'all' && <Chip>{filterOverdue === 'overdue' ? 'Overdue only' : 'Not overdue'}</Chip>}
          {outstandingOnly && <Chip>Outstanding only</Chip>}
          {dateFilter.start && <Chip>Inv from: {dateFilter.start}</Chip>}
          {dateFilter.end && <Chip>Inv to: {dateFilter.end}</Chip>}
          {dueDateFilter.start && <Chip>Due from: {dueDateFilter.start}</Chip>}
          {dueDateFilter.end && <Chip>Due to: {dueDateFilter.end}</Chip>}
          {totalRange.min !== '' && <Chip>Total ≥ {totalRange.min}</Chip>}
          {totalRange.max !== '' && <Chip>Total ≤ {totalRange.max}</Chip>}
        </div>
      )}
    </div>
  );
};

const Chip = ({ children }) => (
  <span className="px-2 py-0.5 bg-blue-100 rounded">{children}</span>
);

export default InvoiceFilters;
